/**
 * Review card — the open card, at the end of the chat, for everything the agent is waiting on in a
 * turn: clarifying questions and library matches, one step each, then a review
 * screen and a single Confirm that sends ONE reply. State and copy come from
 * {@link useReviewCard}, called by the host so that closing the card (which
 * swaps it for a {@link ReviewSkippedCard} in the transcript) keeps the picks.
 */

import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded';
import { Box, Button, Stack, Typography } from '@mui/material';

import { ReviewQuestionStep } from './ReviewQuestionStep';
import { ReviewStepHeader } from './ReviewStepHeader';
import { reviewCardFrameSx } from './reviewStyles';
import { ReviewSummaryRow } from './ReviewSummaryRow';
import { VariableMatchStep } from './VariableMatchStep';
import type { UseReviewCardResult } from '../types/review-types';

interface ReviewCardProps {
  card: UseReviewCardResult;
}

export const ReviewCard = ({ card }: ReviewCardProps) => {
  const { currentItem, currentPick } = card;

  return (
    <Stack spacing={3} data-testid="review-card" sx={reviewCardFrameSx}>
      <ReviewStepHeader
        icon={
          <ChecklistRoundedIcon
            sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }}
            aria-hidden
          />
        }
        title={card.title}
        positionLabel={card.positionLabel}
        previousLabel={card.previousLabel}
        nextLabel={card.nextLabel}
        canGoBack={card.canGoBack}
        canGoNext={card.canGoNext}
        onBack={card.onBack}
        onNext={card.onNext}
        onClose={card.onClose}
        closeLabel={card.closeLabel}
      />
      <Typography variant="body2">{card.isReview ? card.reviewIntro : card.intro}</Typography>

      {currentItem?.kind === 'question' && (
        <ReviewQuestionStep
          question={currentItem.question}
          questionNumber={card.questionNumber}
          questionCount={card.questionCount}
          pick={currentPick}
          draft={card.drafts[currentItem.id] ?? ''}
          disabled={card.disabled}
          onSelect={card.onSelect}
          onDraftChange={(text) => card.onDraftChange(currentItem.id, text)}
        />
      )}

      {currentItem?.kind === 'match' && (
        <VariableMatchStep
          checkpoint={currentItem.checkpoint}
          selection={
            currentPick?.type === 'use' || currentPick?.type === 'build' ? currentPick : null
          }
          buildNewLabel={card.buildNewLabel}
          disabled={card.disabled}
          onSelect={card.onSelect}
        />
      )}

      {card.isReview && (
        <>
          <Stack>
            {card.summaryEntries.map((entry) => (
              <ReviewSummaryRow
                key={entry.itemId}
                entry={entry}
                buildNewLabel={card.buildNewLabel}
              />
            ))}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="small"
              onClick={card.onConfirm}
              disabled={card.disabled}
              data-testid="review-confirm"
              sx={{ textTransform: 'none' }}
            >
              {card.confirmLabel}
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
};
